// hypercomb-runtime/src/dependency-loader.ts

import { EffectBus } from '@hypercomb/core'
import { Store } from './store'
import { activeInstallIndex } from './install-index.js'

/** Line 2 of an atom or atomized barrel, written by the essentials build
 *  (`LAZY_MARKER_LINE` in build-module.ts). */
const LAZY_MARKER = '// lazy'

/** True when the dependency is an atom: its SECOND line carries the marker.
 *  Line 1 stays the alias every other reader parses. */
export const isLazyDependency = (bytes: Uint8Array): boolean => {
  const head = new TextDecoder().decode(bytes.subarray(0, 512)).split('\n', 3)
  return (head[1] ?? '').startsWith(LAZY_MARKER)
}

export class DependencyLoader extends EventTarget {

  private get store(): Store { return <Store>get("@hypercomb.social/Store") }
  readonly #loaded = new Set<string>()
  #loading: Promise<void> | null = null

  #dependencyCount = 0
  #loadedSignatures: readonly string[] = []
  #failedSignatures: readonly string[] = []

  public get dependencyCount(): number { return this.#dependencyCount }
  public get loadedSignatures(): readonly string[] { return this.#loadedSignatures }
  public get failedSignatures(): readonly string[] { return this.#failedSignatures }

  public load = async (): Promise<void> => {
    // In-flight dedup: if load() is already running, wait for it
    if (this.#loading) {
      console.log('[dependency-loader] load() already in progress, waiting')
      return this.#loading
    }

    const run = async (): Promise<void> => {
      // Use cached alias map from resolveImportMap() if available (skips OPFS re-scan)
      let pending = await this.#collectPending()
      if (!pending.length) {
        console.log('[dependency-loader] no pending dependencies')
        return
      }

      // When beeDeps is present, only eagerly load deps NOT claimed by any bee.
      // Bee-specific deps are lazy-loaded by ScriptPreloader.#ensureDeps().
      const beeDeps = (globalThis as any).__hypercombBeeDeps as Record<string, string[]> | undefined
      if (beeDeps) {
        const claimed = new Set(Object.values(beeDeps).flat())
        const before = pending.length
        pending = pending.filter(p => {
          const pureSig = p.sig.replace(/\.js$/i, '')
          return !claimed.has(pureSig) && !claimed.has(p.sig)
        })
        if (before !== pending.length) {
          console.log(`[dependency-loader] ${before - pending.length} deps claimed by bees, ${pending.length} orphans to eagerly load`)
        }
        if (!pending.length) return
      }

      EffectBus.emit('loader:deps-start', { total: pending.length })

      // Verify signatures then import concurrently
      const results = await Promise.allSettled(
        pending.map(({ sig, alias }) =>
          this.#verifyAndImport(sig, alias)
        )
      )

      const loadedSigs: string[] = []
      const failedSigs: string[] = []
      let lazyCount = 0

      for (let i = 0; i < results.length; i++) {
        const r = results[i]
        const { sig } = pending[i]

        if (r.status === 'fulfilled' && r.value === 'lazy') {
          lazyCount++
        } else if (r.status === 'fulfilled') {
          this.#loaded.add(sig)
          loadedSigs.push(sig)
        } else {
          console.error(`[dependency-loader] failed to load dependency ${sig}:`, r.reason)
          failedSigs.push(sig)
        }
      }

      if (lazyCount) console.log(`[dependency-loader] ${lazyCount} atom(s) left to load through the import map on demand`)

      this.#dependencyCount = this.#dependencyCount + loadedSigs.length
      this.#loadedSignatures = [...this.#loadedSignatures, ...loadedSigs]
      this.#failedSignatures = [...this.#failedSignatures, ...failedSigs]
      this.dispatchEvent(new CustomEvent('change'))

      EffectBus.emit('loader:deps-done', {
        loaded: loadedSigs.length,
        failed: failedSigs.length,
        total: pending.length,
      })
    }

    this.#loading = run()
    try { await this.#loading } finally { this.#loading = null }
  }

  #collectPending = async (): Promise<{ sig: string; alias: string }[]> => {
    // Fast path: use cached alias map from resolveImportMap() (web mode)
    const cachedMap = (globalThis as any).__hypercombAliasMap as Map<string, string> | undefined
    if (cachedMap && cachedMap.size > 0) {
      const pending: { sig: string; alias: string }[] = []
      for (const [alias, sig] of cachedMap) {
        if (this.#loaded.has(sig)) {
          console.log(`[dependency-loader] ${alias} (${sig}) already loaded, skipping`)
          continue
        }
        pending.push({ sig, alias })
      }
      return pending
    }

    // Fallback: scan the sign('dependencies') pool (dev mode or no cached
    // map), UNIONED with the legacy `__dependencies__` drain dir while it
    // exists — the Store's detached absorb may still be moving files on
    // the first post-upgrade boot.
    if (!this.store.opfsAvailable) return []
    const depDirs = [this.store.dependencies, this.store.legacyDependencies]
      .filter((d): d is FileSystemDirectoryHandle => !!d)
    if (!depDirs.length) return []

    const pending: { sig: string; alias: string }[] = []
    const seen = new Set<string>()

    for (const depDir of depDirs) {
      try {
        for await (const [sig, entry] of depDir.entries()) {
          if (entry.kind !== 'file') continue
          if (!this.#isSignature(sig)) continue
          if (seen.has(sig)) continue   // mid-drain: same file in both locations
          seen.add(sig)
          if (this.#loaded.has(sig)) {
            console.log(`[dependency-loader] ${sig} already loaded, skipping`)
            continue
          }

          try {
            const file = await (entry as FileSystemFileHandle).getFile()
            const prefix = await file.slice(0, 512).arrayBuffer()
            const first = new TextDecoder().decode(prefix).split('\n', 1)[0]?.trim() ?? ''

            const alias = this.#readAliasFromFirstLine(first)
            if (alias) pending.push({ sig, alias })
          } catch {
            // skip unreadable entries
          }
        }
      } catch {
        // legacy dir vanished mid-drain — the pool holds everything
      }
    }

    return pending
  }

  #verifyAndImport = async (sig: string, alias: string): Promise<'imported' | 'lazy'> => {
    const pureSig = sig.replace(/\.js$/i, '')

    // Import from verified OPFS bytes via a self-typed blob URL — the same
    // pattern Store.getBee uses for bees, and for the same reason: the
    // CALLER, not the network, is responsible for knowing the MIME type of
    // what it is about to import. `import(alias)` for a participant resolves
    // to a network `/opfs/<sig>` URL whose type depends on the service
    // worker being in control of the page. A page is UNCONTROLLED on its
    // first navigation (first visit, or right after the worker updates), so
    // that fetch falls through to the host's own routing — an SPA catch-all
    // answers `text/html` for it — and the browser refuses to load it as a
    // module. That is exactly the "blank until a hard refresh" failure: a
    // hard refresh is a fresh navigation, which the by-then-active worker
    // does control. Reading the verified bytes ourselves and blob-wrapping
    // them removes the dependency on the worker (and the host) entirely.
    const bytes = this.store.opfsAvailable ? await this.store.getDependencyBytes(pureSig) : null
    // AN ATOM IS NEVER IMPORTED HERE. It registers nothing, so it has no
    // reason to load at boot; and a blob import would be a SECOND instance
    // beside the one every importer reaches through the import map
    // (documentation/atomic-modules-plan.md, step 4). It loads once, on
    // demand, when a bee imports its specifier.
    if (bytes && isLazyDependency(bytes)) return 'lazy'
    // A visitor installed from the index holds no dependency bytes; the index
    // says which are atoms (install-index.ts).
    if (!bytes && activeInstallIndex()?.lazy.includes(pureSig)) return 'lazy'
    console.log(`[dependency-loader] importing ${alias} (${pureSig})`)
    if (bytes) {
      const exact = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
      const blobUrl = URL.createObjectURL(new Blob([exact], { type: 'application/javascript' }))
      try {
        await import(/* @vite-ignore */ blobUrl)
      } finally {
        URL.revokeObjectURL(blobUrl)
      }
    } else {
      // No verified local bytes to build a blob from — e.g. a read-only
      // visitor, whose `alias` is already a self-typed `blob:` URL minted by
      // resolveImportMap. Import it as-is; that path was never SW-dependent.
      await import(/* @vite-ignore */ alias)
    }
    console.log(`[dependency-loader] imported ${alias}`)
    return 'imported'
  }

  #readAliasFromFirstLine = (text: string): string | null => {
    if (!text.startsWith('//')) return null
    const parts = text.split(/\s+/)
    const token = (parts[1] ?? '').trim()
    return token.startsWith('@') ? token : null
  }

  #isSignature = (name: string): boolean =>
    /^[a-f0-9]{64}$/i.test(name.replace('.js', ''))
}

register('@hypercomb.social/DependencyLoader', new DependencyLoader())
